import React, { useState } from 'react';
import { UploadCloudIcon, FileSpreadsheetIcon, CheckCircleIcon, AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { UploadDropzone } from '../../../components/ui/upload-dropzone';
interface GenericCsvFormProps {
  onImportComplete: () => void;
}
const AFTERTIDE_FIELDS = [{
  value: 'ignore',
  label: 'Ignore this column'
}, {
  value: 'title',
  label: 'Title *'
}, {
  value: 'author',
  label: 'Author *'
}, {
  value: 'isbn',
  label: 'ISBN'
}, {
  value: 'isbn13',
  label: 'ISBN-13'
}, {
  value: 'genre',
  label: 'Genre'
}, {
  value: 'seriesStatus',
  label: 'Series / Standalone'
}, {
  value: 'rating',
  label: 'My Rating'
}, {
  value: 'review',
  label: 'My Review'
}, {
  value: 'status',
  label: 'Status / Shelf'
}, {
  value: 'dateStarted',
  label: 'Date Started'
}, {
  value: 'dateFinished',
  label: 'Date Finished'
}, {
  value: 'dateAdded',
  label: 'Date Added'
}, {
  value: 'tags',
  label: 'Bookshelves / Tags'
}, {
  value: 'mood',
  label: 'Mood Tags'
}, {
  value: 'pacing',
  label: 'Pacing'
}, {
  value: 'warnings',
  label: 'Content Warnings'
}, {
  value: 'notes',
  label: 'Notes'
}];
export const GenericCsvForm: React.FC<GenericCsvFormProps> = ({
  onImportComplete
}) => {
  const dk = useDarkClasses();
  const [isParsing, setIsParsing] = useState(false);
  const [step, setStep] = useState<'upload' | 'mapping'>('upload');
  // Mock detected headers and initial mapping
  const detectedHeaders = ['Book Title', 'Writer', 'Stars', 'Finished On', 'Personal Notes', 'Genre', 'Series Info', 'Source Link'];
  const initialMapping: Record<string, string> = {
    'Book Title': 'title',
    Writer: 'author',
    Stars: 'rating',
    'Finished On': 'dateFinished',
    'Personal Notes': 'review',
    Genre: 'genre',
    'Series Info': 'seriesStatus',
    'Source Link': 'ignore'
  };
  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);
  const handleUploadComplete = (files: any) => {
    setIsParsing(true);
    // Simulate parsing
    setTimeout(() => {
      setIsParsing(false);
      setStep('mapping');
    }, 1500);
  };
  const handleMappingChange = (header: string, field: string) => {
    setMapping(prev => ({
      ...prev,
      [header]: field
    }));
  };
  const handleReset = () => {
    setStep('upload');
    setMapping(initialMapping);
  };
  const isTitleMapped = Object.values(mapping).includes('title');
  const isAuthorMapped = Object.values(mapping).includes('author');
  const isValid = isTitleMapped && isAuthorMapped;
  return <div className="space-y-8" data-id="element-10904" data-mp="8teei20lt9ko">
      <div className={`p-6 rounded-xl border ${dk.card} ${dk.borderSubtle}`} data-id="element-10905" data-mp="8smk2uiqthvz">
        <h3 className={`font-bold mb-2 flex items-center ${dk.textHeading}`} data-id="element-10906" data-mp="4s2mxqy66e9u">
          <FileSpreadsheetIcon className="w-5 h-5 mr-2 text-indigo-500" data-id="element-10907" data-mp="cc3qcqkmk2n2" />
          Upload Any CSV
        </h3>
        <p className={`text-sm ${dk.textSecondary}`} data-id="element-10908" data-mp="hchn3cgrzq5s">
          Upload any CSV from any reading app — Goodreads, StoryGraph,
          LibraryThing, Bookly, your own spreadsheet, anything with title and
          author columns. We'll figure out the rest.
        </p>
      </div>

      {step === 'upload' && <div className={isParsing ? 'opacity-50 pointer-events-none' : ''} data-id="element-10909" data-mp="v3d2l7ls55ic">
          <UploadDropzone onUploadComplete={handleUploadComplete} images={[]} maxFiles={1} accept=".csv,text/csv" label="Click to upload or drag and drop your CSV" helperText="Any CSV from any reading app — we'll figure out the columns" data-id="element-10910" data-mp="1027m6brkmy3i" />
          {isParsing && <p className="text-center mt-4 text-sm font-medium text-indigo-600 animate-pulse" data-id="element-10911" data-mp="1dzmfvsouxs1a">
              Analyzing columns...
            </p>}
        </div>}

      {step === 'mapping' && <div className={`rounded-xl border ${dk.card} ${dk.border} overflow-hidden`} data-id="element-10912" data-mp="3maf15je58scm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-10913" data-mp="3hbc8yzj9igm2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-10914" data-mp="3hdewncwxzxnm">
              <div data-id="element-10915" data-mp="3cebxg93n8lnk">
                <h3 className={`font-bold text-lg ${dk.textHeading}`} data-id="element-10916" data-mp="duc56awe8s44">
                  Map your columns
                </h3>
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-10917" data-mp="8v91ouw2aty9">
                  Tell us how each column in your CSV should be converted into
                  your AfterTide library. We've made some guesses — adjust if
                  needed.
                </p>
              </div>
              <button onClick={handleReset} className={`flex items-center text-sm font-medium ${dk.textMuted} hover:${dk.textPrimary} transition-colors`} data-id="element-10918" data-mp="wlmtqlencssu">
                <RefreshCwIcon className="w-4 h-4 mr-1.5" data-id="element-10919" data-mp="103iu5vork86r" />
                Re-upload file
              </button>
            </div>

            {isValid ? <div className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm font-medium" data-id="element-10920" data-mp="146p1l6nm45et">
                <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-10921" data-mp="1e3tnr6hgk0vm" />
                All required fields mapped
              </div> : <div className="flex items-center p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm font-medium" data-id="element-10922" data-mp="1izsjjf548l62">
                <AlertTriangleIcon className="w-4 h-4 mr-2" data-id="element-10923" data-mp="1rtx4jhxkj2do" />
                Map Title and Author to continue
              </div>}
          </div>

          <div className="overflow-x-auto" data-id="element-10924" data-mp="1wv4bxfvstzjt">
            <table className="w-full text-sm text-left" data-id="element-10925" data-mp="21tfnbv1qsi45">
              <thead className={`text-xs uppercase bg-gray-50 dark:bg-gray-800/50 ${dk.textMuted}`} data-id="element-10926" data-mp="26rh58cnfmbe1">
                <tr data-id="element-10927" data-mp="1h2it3701tlvu">
                  <th className="px-6 py-4 font-semibold" data-id="element-10928" data-mp="1h43iyjbf4gnd">Your CSV Column</th>
                  <th className="px-6 py-4 font-semibold" data-id="element-10929" data-mp="1c50i7b2h0wl7">Convert to</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-10930" data-mp="1qtwy1uarrdwd">
                {detectedHeaders.map(header => <tr key={header} data-id="element-10931" data-mp="1m0byhxtj2bv2">
                    <td className="px-6 py-4" data-id="element-10932" data-mp="1zta5yij8vuez">
                      <span className={`font-mono text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 ${dk.textPrimary}`} data-id="element-10933" data-mp="1zryudwjk5idb">
                        {header}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-10934" data-mp="24smv59xl9dkq">
                      <select value={mapping[header] || 'ignore'} onChange={e => handleMappingChange(header, e.target.value)} className={`w-full max-w-xs rounded-lg border text-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${dk.isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-10935" data-mp="24qtjthc1656e">
                        {AFTERTIDE_FIELDS.map(field => <option key={field.value} value={field.value} className={field.label.includes('*') ? 'text-red-500 font-medium' : ''} data-id="element-10936" data-mp="2hagv0avjohl1">
                            {field.label}
                          </option>)}
                      </select>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30" data-id="element-10937" data-mp="3sokxk5g77e1p">
            <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-10938" data-mp="2flnbozgujyzz">
              Dates in any common format will be automatically converted to
              DD/MM/YYYY (Australian format).
            </p>
            <div className="flex justify-end" data-id="element-10939" data-mp="26peelrgcdch0">
              <button onClick={onImportComplete} disabled={!isValid} className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${isValid ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'}`} data-id="element-10940" data-mp="21t7cyauzan4v">
                Continue to Preview
              </button>
            </div>
          </div>
        </div>}
    </div>;
};